// ============================================================================
// SHARED SEO CONFIGURATION
// ============================================================================
// Used by vite/seo-inject-plugin at BUILD TIME (Node.js ESM context).
// Cannot import JSON from @infinitro/content here due to Node.js ESM constraints.
// Runtime SEO uses seo.ts which reads from the @infinitro/content seoContent store.
//
// Keep in sync with libs/content/src/lib/seo/en.json.
// ============================================================================
// Placeholder token for company name substitution - matches {brand} in en.json
export const COMPANY_NAME_PLACEHOLDER = '{brand}';
// Takes the theme rather than reading the env: this module is loaded by the app and by the build
// plugin, which have `import.meta.env` and `process.env` respectively.
const SPORTS_FREE_THEMES = ['turboslots', 'luna'];
export function hasSportsCopy(theme) {
    return !SPORTS_FREE_THEMES.includes(theme ?? '');
}
export const SPORTS_ONLY_ROUTES = ['/sports', '/terms-sports', '/special-bets'];
export const casinoOnlySeoConfig = {
    '/': {
        title: '{brand} | Slots, Live Casino & Fast Payouts',
        description: 'Slots, live casino and provably fair originals at {brand}, with instant crypto withdrawals on one wallet.',
        keywords: 'crypto casino, bitcoin casino, crypto slots, live dealer casino, instant crypto withdrawals, provably fair casino'
    },
    '/about-us': {
        title: 'About Us | Learn More About {brand}',
        description: 'Learn about {brand}, a leading crypto casino. Committed to secure, fair & thrilling gambling with instant crypto withdrawals.'
    },
    '/faq/games-sports': {
        title: '{brand} Games | FAQ',
        description: 'Casino games at {brand}: providers, provable fairness, bonus features & payouts. Common questions answered.'
    }
};
// Re-export domain-specific configs from dedicated files
export { sportSeoPatterns, sportsSlugMap } from './seo-config-sports.js';
export { providerSeoPatterns, providersSlugMap } from './seo-config-providers.js';
// Static route → SEO data map (title/description with {brand} placeholder)
export const seoConfig = {
    '/': {
        title: '{brand} Login | Instant Crypto Withdrawals',
        description: '{brand} login: sign in to play slots, live casino and sports on one wallet, with instant crypto withdrawals and provably fair originals.',
        keywords: 'crypto casino, bitcoin casino, crypto sportsbook, bitcoin sports betting, instant crypto withdrawals, provably fair casino'
    },
    '/casino': {
        title: '{brand} Online Casino | Slots, Live Dealers & Fast Payouts',
        description: 'Slots, blackjack, roulette and live dealer tables at {brand}. Fast crypto deposits, instant withdrawals and support around the clock.',
        keywords: 'crypto casino, bitcoin casino games, online casino, crypto slots, live dealer casino, blackjack, roulette'
    },
    '/sports': {
        title: '{brand} Online Sportsbook | Crypto Sports Betting & Odds',
        description: 'Bet on sports at {brand}. Competitive odds on football, basketball & more. Live betting, secure crypto wagering & fast payouts.',
        keywords: 'crypto sportsbook, bitcoin sports betting, live betting, football odds, basketball betting, esports betting'
    },
    '/about-us': {
        title: 'About Us | Learn More About {brand}',
        description: 'Learn about {brand}, a leading crypto casino & sportsbook. Committed to secure, fair & thrilling gambling with instant crypto withdrawals.'
    },
    '/affiliate': {
        title: 'Affiliate Program | Earn Crypto with {brand}',
        description: 'Partner with {brand} & earn commissions promoting our crypto casino. Competitive rates, real-time tracking & dedicated affiliate support.'
    },
    '/aml-policy': {
        title: 'AML Policy | Anti-Money Laundering at {brand}',
        description: "{brand}'s AML policy ensures a secure, trustworthy environment. Read about our measures to prevent illicit activity on our platform."
    },
    '/bonus-terms': {
        title: '{brand} Bonus Terms | Welcome & No Deposit Bonus Rules',
        description: "Read the rules for {brand}'s casino bonuses & promotions. Learn how to claim, use & withdraw bonus winnings from our platform."
    },
    '/complaints-and-arbitration': {
        title: 'Complaints and Arbitration | Player Support at {brand}',
        description: "Find {brand}'s complaint & dispute resolution process. We ensure fair treatment and resolve player issues quickly and transparently."
    },
    '/contact': {
        title: 'Contact | Support & Help at {brand}',
        description: 'Contact {brand} support, member services, and finance by email. We are available 24/7 to answer your questions and suggestions.'
    },
    '/fairness': {
        title: 'Provably Fair Games | Ensure Fairness at {brand}',
        description: '{brand} uses provably fair technology to guarantee every game result is verifiable, transparent & completely random.'
    },
    '/featurebuyin': {
        title: '{brand} Bonus Buy Slots | Buy the Bonus Round Instantly',
        description: 'Buy straight into the bonus round at {brand}. Bonus buy slots from Pragmatic Play, Hacksaw Gaming, Nolimit City and more, playable with crypto.'
    },
    '/gameshows': {
        title: '{brand} Game Shows | Crazy Time, Mega Wheel & Live Hosts',
        description: 'Play Crazy Time, Mega Wheel, Monopoly Live and more game shows at {brand}. Real hosts, live rounds and big multipliers, 24/7 with crypto.'
    },
    '/kyc-policy': {
        title: 'KYC Policy | Know Your Customer at {brand}',
        description: "{brand}'s KYC policy outlines our identity verification process. Learn how we protect players and maintain a secure gaming environment."
    },
    '/livecasino': {
        title: '{brand} Live Casino | Real Dealers, Blackjack & Roulette',
        description: 'Live blackjack, roulette, baccarat and game shows at {brand}. Real dealers, HD streams and tables open 24/7, all playable with crypto.',
        keywords: 'live casino, live dealer games, live blackjack, live roulette, live baccarat, casino game shows'
    },
    '/newgames': {
        title: '{brand} New Games | Latest Slot & Live Casino Releases',
        description: 'The newest slot and live casino releases at {brand}, added as providers launch them. Be among the first to play them with crypto.'
    },
    '/payment-policy': {
        title: '{brand} Payment Policy | Crypto Deposits & Withdrawals',
        description: '{brand} accepts crypto deposits & withdrawals. Read our payment policy for transaction limits, accepted coins & processing times.'
    },
    '/populargames': {
        title: '{brand} Popular Games | Most Played Slots & Live Tables',
        description: 'See what players are playing most at {brand} right now: the top slots, live tables and game shows in one list, all playable with crypto.'
    },
    '/originals': {
        title: '{brand} Originals | Crash, Plinko & Provably Fair Games',
        description: "Play {brand} Originals: Crash, Plinko, Keno, Dragon's Tower and more. In-house provably fair games with results you can verify yourself."
    },
    '/privacy-policy': {
        title: 'Privacy Policy | Data Protection at {brand}',
        description: "{brand}'s privacy policy. Learn how we collect, use & protect your personal data on our crypto casino platform."
    },
    '/promotion-page': {
        title: '{brand} Casino Bonus, Free Spins & Promo Codes',
        description: 'Claim the {brand} welcome bonus, free spins and promo codes, plus weekly races and tournaments. Check the current terms and claim yours.',
        keywords: 'casino promotions, crypto casino bonuses, weekly race, casino tournaments'
    },
    '/providers': {
        title: '{brand} Game Providers | Partnered Studios & Software',
        description: 'Explore all game providers at {brand}. We partner with top studios to bring you a diverse, high-quality crypto casino library.'
    },
    '/referral': {
        title: '{brand} Referral Program | Invite Friends & Earn Rewards',
        description: 'Refer friends to {brand} & earn rewards when they sign up and play. Share your link and collect bonuses for every referral.',
        keywords: 'casino referral program, refer a friend bonus, affiliate rewards'
    },
    '/responsible-gaming': {
        title: 'Responsible Gaming | Play Safely at {brand}',
        description: '{brand} is committed to responsible gambling. Access tools & resources to manage your gaming habits and play safely.'
    },
    '/slots': {
        title: '{brand} Online Slots | Free Demos, RTP & Big Max Wins',
        description: 'Spin thousands of slots at {brand} from Pragmatic Play, Hacksaw Gaming and Nolimit City. Try any game free in demo mode or play with crypto.',
        keywords: 'crypto slots, bitcoin slots, online slots, video slots, megaways slots, feature buy slots'
    },
    '/special-bets': {
        title: 'Special Bets | Crypto Sportsbook at {brand}',
        description: 'Place special bets at {brand}. Sportsbook markets across sports, esports & one-off events, with instant crypto deposits and fast payouts.'
    },
    '/terms-of-service': {
        title: 'Terms of Service | {brand}',
        description: 'Review the terms & conditions governing your use of {brand}. Understand your rights and responsibilities as a player.'
    },
    '/terms-sports': {
        title: 'Sports Betting Terms & Conditions | {brand}',
        description: "{brand}'s sports betting terms & conditions. Rules for placing bets, winning payouts & sportsbook usage on our platform."
    },
    '/transparency': {
        title: '{brand} Transparency | Provably Fair Crypto Casino',
        description: 'See how {brand} operates in the open. Provably fair game verification, published return rates read from the live catalogue & on-chain payouts.'
    },
    '/vip': {
        title: '{brand} VIP Program | Exclusive Rewards & Benefits',
        description: "{brand}'s VIP program: exclusive rewards, personalised bonuses & priority support. Tailored perks for loyal players.",
        keywords: 'casino vip program, rakeback, casino loyalty rewards, high roller casino, vip tiers'
    },
    '/faq': {
        title: '{brand} FAQ | Frequently Asked Questions',
        description: 'Find answers to common questions about {brand}. Browse our FAQ for help with accounts, payments, bonuses & casino games.'
    },
    '/faq/account': {
        title: '{brand} Account | FAQ',
        description: 'Managing your {brand} account: sign-up, login, verification & profile settings. Answers to common account questions.'
    },
    '/faq/about': {
        title: 'About {brand} | FAQ',
        description: 'About {brand}: licensing, how the crypto casino works & what makes the platform fair. Answers to common questions.'
    },
    '/faq/bonuses': {
        title: '{brand} Bonuses | FAQ',
        description: '{brand} bonuses explained: welcome offers, promo codes, wagering requirements & rewards. Common bonus questions answered.'
    },
    '/faq/deposits': {
        title: '{brand} Deposits | FAQ',
        description: 'How to deposit crypto at {brand}: supported coins, networks, minimums & confirmation times. Common deposit questions answered.'
    },
    '/faq/games-sports': {
        title: '{brand} Games & Sports | FAQ',
        description: 'Casino games & sports betting at {brand}: providers, provable fairness, odds & live betting. Common questions answered.'
    },
    '/faq/responsible-gambling': {
        title: '{brand} Responsible Gambling | FAQ',
        description: 'Responsible gambling at {brand}: deposit limits, self-exclusion, cooldowns & support tools to help you play safely.'
    },
    '/faq/security': {
        title: '{brand} Security | FAQ',
        description: '{brand} security: account protection, two-factor authentication, fairness & data privacy. Common security questions answered.'
    },
    '/faq/support-disputes': {
        title: '{brand} Support & Disputes | FAQ',
        description: 'Getting help at {brand}: live chat, response times & how disputes are resolved. Common support questions answered.'
    },
    '/faq/withdrawals': {
        title: '{brand} Withdrawals | FAQ',
        description: 'How to withdraw crypto from {brand}: payout times, minimums, fees & limits. Answers to common withdrawal questions.'
    },
    '/hotslots': {
        title: '{brand} Hot Slots | Trending Casino Slots',
        description: 'The slots players are spinning most right now at {brand}.'
    },
    '/hotlive': {
        title: '{brand} Hot Live Casino | Trending Live Tables',
        description: 'The live dealer tables players are joining most right now at {brand}.'
    },
    '/table_games': {
        title: '{brand} Table Games | Blackjack, Roulette & Poker',
        description: 'Play blackjack, roulette, baccarat & poker with crypto at {brand}.'
    },
    '/favorites': {
        title: 'My Favorites | {brand}',
        description: 'The casino games you have saved to your favorites at {brand}.'
    },
    '/recentlyplayed': {
        title: 'Recently Played | {brand}',
        description: 'The casino games you have played most recently at {brand}.'
    }
};
